/**
 * Retained-geometry terrain for Phaser 4 (docs/PERFORMANCE.md, Phase 1).
 *
 * `Phaser.GameObjects.Graphics` replays its command buffer every frame — Earcut, stroke
 * quads, a CPU copy of every vertex, and a `bufferSubData` upload. `StaticMesh` takes the
 * same triangles once (`TriMeshBuilder` in `src/sim/map/meshBuilder.ts`), uploads them to
 * `STATIC_DRAW` buffers, and draws them with the renderer's own FLAT program (the one
 * Graphics uses) through one `drawElements` per chunk. The only per-frame CPU work is a
 * 4×4 matrix multiply.
 *
 * The FLAT vertex shader is `gl_Position = uProjectionMatrix * vec4(inPosition, 1, 1)` with
 * positions already in camera space, so passing `projection × cameraMatrix × objectMatrix`
 * as that uniform lets world-space vertices go through the unchanged shader. This follows
 * the pattern `SubmitterSpriteGPULayer` uses for its static instance buffer.
 *
 * WebGL only. Under Canvas the pool keeps the Graphics painter.
 */
import * as Phaser from 'phaser';
import { MESH_VERTEX_STRIDE_BYTES, type MeshChunk } from '../../sim/map/index.ts';

const NODE_NAME = 'BattleboatsStaticTriMesh';
const FLAT_HANDLER = 'BatchHandlerTriFlat';

type WebGLRendererInternals = Phaser.Renderer.WebGL.WebGLRenderer & {
  gl: WebGLRenderingContext;
  glWrapper: { updateVAO: (state: { vao: unknown }) => void };
  renderNodes: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager;
  projectionMatrix: Phaser.Math.Matrix4;
  glVAOWrappers: unknown[];
  shaderProgramFactory: unknown;
};

type ProgramSuite = {
  program: Phaser.Renderer.WebGL.Wrappers.WebGLProgramWrapper;
};

type FlatProgramManager = {
  getCurrentProgramSuite: () => ProgramSuite | null;
  setUniform: (name: string, value: unknown) => void;
  applyUniforms: (program: Phaser.Renderer.WebGL.Wrappers.WebGLProgramWrapper) => void;
};

type FlatHandler = {
  programManager: FlatProgramManager;
};

type LayoutWrapperCtor = new (
  renderer: Phaser.Renderer.WebGL.WebGLRenderer,
  layout: unknown,
  buffer: Phaser.Renderer.WebGL.Wrappers.WebGLBufferWrapper | null,
) => Phaser.Renderer.WebGL.Wrappers.WebGLVertexBufferLayoutWrapper;

/** One uploaded chunk: vertex + index buffers and a VAO per FLAT program variant. */
class GpuChunk {
  readonly indexCount: number;
  private readonly renderer: WebGLRendererInternals;
  private readonly vertexBuffer: Phaser.Renderer.WebGL.Wrappers.WebGLBufferWrapper;
  private readonly indexBuffer: Phaser.Renderer.WebGL.Wrappers.WebGLBufferWrapper;
  private readonly layout: Phaser.Renderer.WebGL.Wrappers.WebGLVertexBufferLayoutWrapper;
  private readonly vaos = new Map<
    Phaser.Renderer.WebGL.Wrappers.WebGLProgramWrapper,
    Phaser.Renderer.WebGL.Wrappers.WebGLVAOWrapper
  >();

  constructor(renderer: WebGLRendererInternals, chunk: MeshChunk) {
    this.renderer = renderer;
    this.indexCount = chunk.indices.length;
    const gl = renderer.gl;

    // An ELEMENT_ARRAY_BUFFER bind is VAO state; make sure no VAO catches it.
    renderer.glWrapper.updateVAO({ vao: null });

    this.vertexBuffer = renderer.createVertexBuffer(chunk.vertexData, gl.STATIC_DRAW);
    this.indexBuffer = renderer.createIndexBuffer(
      chunk.indices.buffer as ArrayBuffer,
      gl.STATIC_DRAW,
    );

    const LayoutWrapper = Phaser.Renderer.WebGL.Wrappers
      .WebGLVertexBufferLayoutWrapper as unknown as LayoutWrapperCtor;

    this.layout = new LayoutWrapper(
      renderer,
      {
        usage: gl.STATIC_DRAW,
        count: chunk.vertexCount,
        layout: [
          { name: 'inPosition', size: 2, type: gl.FLOAT, normalized: false },
          { name: 'inTint', size: 4, type: gl.UNSIGNED_BYTE, normalized: true },
        ],
      },
      this.vertexBuffer,
    );

    if (this.layout.layout.stride !== MESH_VERTEX_STRIDE_BYTES) {
      throw new Error(
        `StaticMesh layout stride ${this.layout.layout.stride} != ${MESH_VERTEX_STRIDE_BYTES}`,
      );
    }
  }

  vao(
    program: Phaser.Renderer.WebGL.Wrappers.WebGLProgramWrapper,
  ): Phaser.Renderer.WebGL.Wrappers.WebGLVAOWrapper {
    const cached = this.vaos.get(program);

    if (cached) {
      return cached;
    }

    const vao = this.renderer.createVAO(
      program,
      this.indexBuffer,
      [this.layout as unknown as Phaser.Types.Renderer.WebGL.WebGLAttributeBufferLayout],
    );
    this.vaos.set(program, vao);
    return vao;
  }

  destroy(): void {
    for (const vao of this.vaos.values()) {
      Phaser.Utils.Array.Remove(this.renderer.glVAOWrappers, vao);
      vao.destroy();
    }

    this.vaos.clear();
    this.renderer.deleteBuffer(this.vertexBuffer);
    this.renderer.deleteBuffer(this.indexBuffer);
  }
}

/**
 * Render node: flushes the pending batch, sets the combined matrix on the FLAT program,
 * and issues one `drawElements` per chunk of the mesh being rendered.
 */
export class StaticTriMeshNode extends Phaser.Renderer.WebGL.RenderNodes.RenderNode {
  private readonly combined = new Float32Array(16);
  private flat: FlatHandler | null = null;

  constructor(manager: Phaser.Renderer.WebGL.RenderNodes.RenderNodeManager) {
    super(NODE_NAME, manager);
  }

  static install(renderer: WebGLRendererInternals): StaticTriMeshNode {
    const existing = renderer.renderNodes.getNode(NODE_NAME);

    if (existing) {
      return existing as StaticTriMeshNode;
    }

    const node = new StaticTriMeshNode(renderer.renderNodes);
    renderer.renderNodes.addNode(NODE_NAME, node);
    return node;
  }

  override run(
    drawingContext: Phaser.Renderer.WebGL.DrawingContext,
    mesh: StaticMesh,
    parentMatrix?: Phaser.GameObjects.Components.TransformMatrix,
  ): void {
    const chunks = mesh.gpuChunks;

    if (chunks.length === 0) {
      return;
    }

    const renderer = this.manager.renderer as WebGLRendererInternals;
    const flat = this.flatHandler(renderer);
    const camera = drawingContext.camera;

    if (!flat || !camera) {
      return;
    }

    const suite = flat.programManager.getCurrentProgramSuite();

    if (!suite) {
      // Still compiling in parallel; skip this frame like BatchHandler does.
      return;
    }

    this.onRunBegin(drawingContext);
    // Anything batched before us (Graphics, sprites) must hit the framebuffer first so
    // depth order survives.
    this.manager.startStandAloneRender();

    const calc = Phaser.GameObjects.GetCalcMatrix(mesh, camera, parentMatrix, true).calc;
    renderer.setProjectionMatrixFromDrawingContext(drawingContext);
    multiplyProjection(this.combined, renderer.projectionMatrix.val, calc);

    const program = suite.program;
    flat.programManager.setUniform('uProjectionMatrix', this.combined);
    flat.programManager.applyUniforms(program);

    const gl = renderer.gl;

    for (const chunk of chunks) {
      renderer.drawElements(
        drawingContext,
        [],
        program,
        chunk.vao(program),
        chunk.indexCount,
        0,
        gl.TRIANGLES,
      );
    }

    this.onRunEnd(drawingContext);
  }

  private flatHandler(renderer: WebGLRendererInternals): FlatHandler | null {
    if (this.flat) {
      return this.flat;
    }

    const node = renderer.renderNodes.getNode(FLAT_HANDLER) as unknown as FlatHandler | null;
    this.flat = node && node.programManager ? node : null;
    return this.flat;
  }
}

/** `out = projection × [a c 0 e; b d 0 f; 0 0 1 0; 0 0 0 1]` (column-major). */
function multiplyProjection(
  out: Float32Array,
  projection: Float32Array | number[],
  m: Phaser.GameObjects.Components.TransformMatrix,
): void {
  const p = projection;
  const { a, b, c, d, e, f } = m;

  // Column 0: M col0 = (a, b, 0, 0)
  out[0] = p[0]! * a + p[4]! * b;
  out[1] = p[1]! * a + p[5]! * b;
  out[2] = p[2]! * a + p[6]! * b;
  out[3] = p[3]! * a + p[7]! * b;
  // Column 1: M col1 = (c, d, 0, 0)
  out[4] = p[0]! * c + p[4]! * d;
  out[5] = p[1]! * c + p[5]! * d;
  out[6] = p[2]! * c + p[6]! * d;
  out[7] = p[3]! * c + p[7]! * d;
  // Column 2: M col2 = (0, 0, 1, 0)
  out[8] = p[8]!;
  out[9] = p[9]!;
  out[10] = p[10]!;
  out[11] = p[11]!;
  // Column 3: M col3 = (e, f, 0, 1)
  out[12] = p[0]! * e + p[4]! * f + p[12]!;
  out[13] = p[1]! * e + p[5]! * f + p[13]!;
  out[14] = p[2]! * e + p[6]! * f + p[14]!;
  out[15] = p[3]! * e + p[7]! * f + p[15]!;
}

/**
 * Static triangle mesh game object. Extends Graphics for its Depth / Visible /
 * Transform / ScrollFactor / Alpha components and so the pool can hold either kind;
 * the command buffer stays empty and `renderWebGL` draws the uploaded chunks instead.
 */
export class StaticMesh extends Phaser.GameObjects.Graphics {
  gpuChunks: GpuChunk[] = [];
  triangleCount = 0;
  private readonly node: StaticTriMeshNode;

  constructor(scene: Phaser.Scene) {
    super(scene);
    this.node = StaticTriMeshNode.install(scene.renderer as WebGLRendererInternals);
  }

  static isSupported(scene: Phaser.Scene): boolean {
    return scene.renderer.type === Phaser.WEBGL;
  }

  /** Replace the uploaded geometry. Pass an empty array to draw nothing. */
  setGeometry(chunks: readonly MeshChunk[]): this {
    // A destroyed mesh (scene shutdown) has no scene; a stale caller painting into it is a
    // no-op, the same as recording commands into a destroyed Graphics used to be.
    if (!this.scene) {
      return this;
    }

    this.releaseChunks();
    const renderer = this.scene.renderer as WebGLRendererInternals;
    let triangles = 0;

    for (const chunk of chunks) {
      if (chunk.indices.length === 0 || chunk.vertexCount === 0) {
        continue;
      }

      this.gpuChunks.push(new GpuChunk(renderer, chunk));
      triangles += chunk.indices.length / 3;
    }

    this.triangleCount = triangles;
    return this;
  }

  /**
   * Replaces the Graphics render step. `GameObject` captures `this.renderWebGL` in its
   * constructor (`addRenderStep`), and a class method is on the prototype by then.
   */
  renderWebGL(
    _renderer: Phaser.Renderer.WebGL.WebGLRenderer,
    src: StaticMesh,
    drawingContext: Phaser.Renderer.WebGL.DrawingContext,
    parentMatrix?: Phaser.GameObjects.Components.TransformMatrix,
  ): void {
    drawingContext.camera?.addToRenderList(src);
    src.node.run(drawingContext, src, parentMatrix);
  }

  /** Graphics declares this in JS only; the display list calls it before destroy. */
  preDestroy(): void {
    this.releaseChunks();
    const base = Phaser.GameObjects.Graphics.prototype as unknown as { preDestroy?: () => void };
    base.preDestroy?.call(this);
  }

  private releaseChunks(): void {
    for (const chunk of this.gpuChunks) {
      chunk.destroy();
    }

    this.gpuChunks = [];
    this.triangleCount = 0;
  }
}
